import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, CheckCircle2, Plane, FileText } from 'lucide-react';
import { fetchVisas, resolveImageUrl } from '../lib/travelApi';

const VisaPage = () => {
  const navigate = useNavigate();
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisas().then(setVisas).catch(() => setVisas([])).finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="visa-page" className="min-h-screen py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <span className="text-amber-600 font-semibold uppercase tracking-wider text-sm">Visa Services</span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">Visiting Visa Assistance</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We handle your visa from start to finish. Here are approximate fees and requirements for popular destinations.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : visas.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            Visa information coming soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {visas.map((visa) => (
              <Card key={visa.id} data-testid={`visa-card-${visa.id}`} className="overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  {visa.image ? (
                    <img src={resolveImageUrl(visa.image)} alt={visa.country} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-amber-100 flex items-center justify-center"><Plane className="w-10 h-10 text-amber-600" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <h3 className="absolute bottom-3 left-4 text-2xl font-bold text-white">{visa.country}</h3>
                  {visa.price > 0 && (
                    <div className="absolute top-3 right-3 bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      ~₹{Number(visa.price).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
                <CardContent className="p-5 flex flex-col flex-1">
                  {visa.processing_time && (
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Clock className="w-4 h-4 mr-2 text-amber-600" /> Processing: {visa.processing_time}
                    </div>
                  )}
                  {visa.requirements && (
                    <div className="space-y-1.5 mb-4 flex-1">
                      <p className="text-sm font-semibold text-gray-800">Requirements:</p>
                      {visa.requirements.split('\n').filter(Boolean).map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <span>{r}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button onClick={() => navigate('/contact', { state: { destination: `${visa.country} Visa`, inquiryType: 'visa' } })}
                    data-testid={`visa-enquire-${visa.id}`}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white mt-auto">
                    Enquire for {visa.country} Visa
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-10 max-w-2xl mx-auto">
          * Prices are approximate and for guidance only. Final visa fees depend on the embassy/consulate, visa type, and processing option.
        </p>
      </div>
    </section>
  );
};

export default VisaPage;
